import axios from "axios";
import { IMPACT_REPORT_PDF_DOWNLOAD_ERROR_MESSAGE } from "@/constants/impactReport";
import type { DashboardReportErrorResponse } from "@/interfaces/impactReport";
import type { ImpactReportPdfDownloadFailure } from "@/interfaces/impactReportPdf";

/**
 * The API's OWN message for a failed client-impact-report request, or a caller-supplied generic
 * fallback when the response carried none.
 *
 * NOT `utils/apiError-helper.ts#apiErrorMessage`: that helper reads a FLAT `{ error: string }` /
 * `{ message: string }` body and has no path into this endpoint's NESTED `{ error: { code,
 * message } }` envelope — it would fall through to axios's own "Request failed with status code
 * N", the exact string a report page exists to keep off screen.
 *
 * THE ONE READER of that envelope: both the page's own error state (`pages/ImpactReport.tsx`)
 * and the card grid's (`ImpactCardGrid.tsx`) call this rather than each parsing the response
 * body themselves, so the two cannot drift into reading it two different ways.
 */
export function resolveImpactReportErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<DashboardReportErrorResponse>(error)) {
    const apiMessage = error.response?.data?.error?.message;
    if (apiMessage) return apiMessage;
  }
  return fallback;
}

/** True when the report was refused because the caller's groups have no campaigns yet. */
export function isNoCampaignsInScopeError(error: unknown): boolean {
  return (
    axios.isAxiosError<DashboardReportErrorResponse>(error) &&
    error.response?.data?.error?.code === "NO_CAMPAIGNS_IN_SCOPE"
  );
}

/**
 * The copy for a failed PDF download, classified by
 * `utils/impact-report-pdf-download-helper.ts`. A converter failure that carries Platform's own
 * reason — a failed job's `error`, or a PDF route's error body — shows that reason verbatim; the
 * generic converter copy stands in only when there is none.
 */
export function resolveImpactReportPdfErrorMessage(
  failure: ImpactReportPdfDownloadFailure,
): string {
  if (failure.kind === "converter" && failure.reason) return failure.reason;
  return IMPACT_REPORT_PDF_DOWNLOAD_ERROR_MESSAGE[failure.kind];
}
